// runtime/film.js
function createFilmPlayer(root, win = window, beforePlay = () => {
}) {
  const find = (selector) => root.querySelector(selector);
  const trigger = find("[data-film-trigger]"), panel = find("[data-film-panel]");
  const template = find("[data-film-video]"), host = find("[data-film-media]");
  const retry = find("[data-film-retry]"), status = find("[data-film-status]");
  const frame = panel.parentElement, listeners = [], mediaListeners = [];
  const dim = win.document.createElement("div");
  dim.className = "bb-film-dim";
  dim.setAttribute("aria-hidden", "true");
  dim.dataset.active = "false";
  win.document.body.append(dim);
  let video = null, session = 0, attempt = 0, wantsPlayback = false, destroyed = false, geometryFrame = null;
  const listen = (element, type, handler, list = listeners, options) => {
    element.addEventListener(type, handler, options);
    list.push(() => element.removeEventListener(type, handler, options));
  };
  const state = (name) => {
    root.dataset.filmState = name;
    retry.hidden = name !== "error";
    status.textContent = { poster: "", paused: "", loading: "Loading film\u2026", playing: "", manual: "Press play in the video controls to watch the film.", error: "The film could not load. Try again." }[name];
  };
  const syncGeometry = () => {
    if (destroyed || panel.hidden) return;
    const clip = win.getComputedStyle(frame).clipPath;
    const match = /^inset\((.*?)\)$/i.exec(clip || "");
    const values = match ? match[1].split(/\s+round\s+/i)[0].trim().split(/\s+/) : ["0"];
    const [top, right = top, bottom = top, left = right] = values;
    panel.style.inset = `${top} ${right} ${bottom} ${left}`;
    const rect = panel.getBoundingClientRect();
    Object.assign(dim.style, { left: `${rect.left}px`, top: `${rect.top}px`, width: `${rect.width}px`, height: `${rect.height}px` });
  };
  const scheduleGeometry = () => {
    syncGeometry();
    if (geometryFrame === null && win.requestAnimationFrame) geometryFrame = win.requestAnimationFrame(() => {
      geometryFrame = null;
      syncGeometry();
    });
  };
  function pause({ hide = false, focus = false } = {}) {
    wantsPlayback = false;
    attempt++;
    dim.dataset.active = "false";
    if (video) video.pause();
    if (root.dataset.filmState !== "error") state(video ? "paused" : "poster");
    if (hide) {
      panel.hidden = true;
      trigger.hidden = false;
    }
    if (focus && trigger.isConnected) trigger.focus({ preventScroll: true });
  }
  const release = () => {
    session++;
    wantsPlayback = false;
    attempt++;
    dim.dataset.active = "false";
    mediaListeners.splice(0).forEach((remove) => remove());
    if (video) {
      video.pause();
      video.removeAttribute("src");
      video.load();
    }
    video = null;
    host.replaceChildren();
  };
  const prepare = () => {
    release();
    const token = session;
    video = template.cloneNode(false);
    const current = video;
    const isCurrent = () => !destroyed && token === session && video === current;
    const poster = find("[data-film-poster]")?.currentSrc || template.dataset.filmPosterSource;
    if (poster) current.setAttribute("poster", poster);
    host.replaceChildren(current);
    listen(current, "play", () => {
      if (!isCurrent() || current.paused || root.dataset.filmState === "error") return;
      beforePlay();
      if (!wantsPlayback) attempt++;
      wantsPlayback = true;
      panel.hidden = false;
      trigger.hidden = true;
      syncGeometry();
    }, mediaListeners);
    listen(current, "playing", () => {
      if (!isCurrent()) return;
      if (!wantsPlayback || current.paused || root.dataset.filmState === "error") {
        current.pause();
        return;
      }
      state("playing");
      syncGeometry();
      dim.dataset.active = "true";
    }, mediaListeners);
    listen(current, "pause", () => {
      if (isCurrent() && current.paused) {
        wantsPlayback = false;
        attempt++;
        dim.dataset.active = "false";
        if (root.dataset.filmState !== "error") state("paused");
      }
    }, mediaListeners);
    listen(current, "ended", () => {
      if (isCurrent()) pause();
    }, mediaListeners);
    listen(current, "error", () => {
      if (isCurrent()) {
        pause();
        state("error");
      }
    }, mediaListeners);
    const small = win.matchMedia("(max-width: 760px)").matches || win.navigator.connection?.saveData;
    current.setAttribute("src", small ? root.dataset.filmMobile : root.dataset.filmDesktop);
    current.load();
  };
  function open() {
    if (destroyed) return;
    beforePlay();
    if (!video || root.dataset.filmState === "error") prepare();
    panel.hidden = false;
    trigger.hidden = true;
    syncGeometry();
    wantsPlayback = true;
    const current = video, token = ++attempt, mediaSession = session;
    state("loading");
    current.focus({ preventScroll: true });
    const rejected = () => {
      if (destroyed || video !== current || session !== mediaSession || token !== attempt || !wantsPlayback || root.dataset.filmState === "error") return;
      wantsPlayback = false;
      dim.dataset.active = "false";
      state("manual");
    };
    try {
      Promise.resolve(current.play()).then(() => {
        if (destroyed || video !== current || session !== mediaSession || !wantsPlayback) current.pause();
      }, rejected);
    } catch {
      rejected();
    }
  }
  listen(trigger, "click", open);
  listen(retry, "click", open);
  const visibleShare = () => {
    const rect = panel.getBoundingClientRect();
    if (!rect.height) return 0;
    return Math.max(0, Math.min(rect.bottom, win.innerHeight || 0) - Math.max(rect.top, 0)) / rect.height;
  };
  listen(win, "scroll", () => {
    if (visibleShare() < 0.5) pause();
    scheduleGeometry();
  }, listeners, { capture: true, passive: true });
  listen(win, "resize", () => {
    if (wantsPlayback && visibleShare() < 0.5) pause();
    scheduleGeometry();
  });
  listen(win, "pagehide", () => pause({ hide: true }));
  listen(win, "keydown", (event) => {
    if (event.key === "Escape" && !panel.hidden) pause({ hide: true, focus: true });
  });
  const resizeObserver = win.ResizeObserver ? new win.ResizeObserver(scheduleGeometry) : null;
  resizeObserver?.observe(frame);
  const mutationObserver = win.MutationObserver ? new win.MutationObserver(scheduleGeometry) : null;
  mutationObserver?.observe(frame, { attributes: true, attributeFilter: ["style", "class"] });
  state("poster");
  return { open, pause, close: () => pause({ hide: true }), destroy() {
    if (destroyed) return;
    destroyed = true;
    release();
    panel.hidden = true;
    trigger.hidden = false;
    state("poster");
    listeners.splice(0).forEach((remove) => remove());
    resizeObserver?.disconnect();
    mutationObserver?.disconnect();
    if (geometryFrame !== null) win.cancelAnimationFrame(geometryFrame);
    dim.remove();
  } };
}
function installFilmPlayers(win = window) {
  if (win.__bbFilmPlayers) return win.__bbFilmPlayers;
  const players = /* @__PURE__ */ new Map(), doc = win.document;
  const pauseOthers = (activeRoot) => {
    for (const [root, player] of players) if (root !== activeRoot) player.pause();
  };
  const scan = () => {
    for (const [root, player] of players) if (!root.isConnected) {
      player.destroy();
      players.delete(root);
    }
    for (const root of doc.querySelectorAll("[data-film-player]")) {
      if (!players.has(root)) players.set(root, createFilmPlayer(root, win, () => pauseOthers(root)));
    }
  };
  doc.addEventListener("shopify:section:load", scan);
  doc.addEventListener("shopify:section:unload", (event) => {
    for (const [root, player] of players) if (event.target.contains(root)) {
      player.destroy();
      players.delete(root);
    }
  });
  win.addEventListener("pageshow", scan);
  win.__bbFilmPlayers = { scan };
  scan();
  return win.__bbFilmPlayers;
}
if (typeof window !== "undefined") installFilmPlayers();
export {
  createFilmPlayer,
  installFilmPlayers
};
